import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import './ClientPortal.css';
import logo from '../assets/branding/abogados_quiroz_logo_exact.svg';

const api = () => (window.__CRM_API__ || process.env.REACT_APP_API_URL || `${window.location.origin}/api`)
  .replace(/\/$/, '').replace(/\/api\/api$/, '/api');

const fileIcon = (mime = '') => {
  if (mime.includes('pdf')) return 'PDF';
  if (mime.startsWith('image/')) return 'IMG';
  if (mime.includes('word') || mime.includes('document')) return 'DOC';
  if (mime.includes('sheet') || mime.includes('excel')) return 'XLS';
  return 'FILE';
};

const fileSize = (value) => {
  const size = Number(value || 0);
  if (!size) return '';
  if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024))} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const portalFileUrl = (token, fileId, download = false) =>
  `${api()}/public/expedientes/${encodeURIComponent(token)}/archivos/${encodeURIComponent(fileId)}${download ? '?download=true' : ''}`;

export default function ClientPortal() {
  // Both legacy /expediente/:token URLs and new /e/:label/:token URLs map to
  // the same secure API. The label is display-only and is never sent as proof
  // of access.
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [file, setFile] = useState(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const loadPortal = async () => {
    const { data: result } = await axios.get(`${api()}/public/expedientes/${encodeURIComponent(token)}`);
    setData(result);
  };

  useEffect(() => {
    loadPortal().catch((error) => setMessage(error.response?.data?.detail || 'No fue posible abrir el expediente virtual.'))
      .finally(() => setLoading(false));
  // The signed URL itself is the portal identity.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const upload = async (event) => {
    event.preventDefault();
    if (!file || uploading) return;
    setUploading(true);
    setMessage('Subiendo archivo al expediente…');
    try {
      const form = new FormData();
      form.append('file', file);
      await axios.post(`${api()}/public/expedientes/${encodeURIComponent(token)}/archivos`, form);
      setMessage('Archivo recibido correctamente por Abogados Quiroz.');
      setFile(null);
      await loadPortal();
    } catch (error) {
      setMessage(error.response?.data?.detail || 'No fue posible cargar el archivo.');
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <main className="client-portal client-portal--loading"><section>Cargando expediente seguro…</section></main>;
  if (!data) return <main className="client-portal client-portal--loading"><section><h1>Enlace no disponible</h1><p>{message}</p></section></main>;

  return (
    <main className="client-portal">
      <section className="client-portal-shell">
        <header className="client-portal-hero">
          <img src={logo} alt="Abogados Quiroz" className="client-portal-logo" />
          <div className="client-portal-eyebrow">EXPEDIENTE DIGITAL SEGURO</div>
          <h1>{data.client.name}</h1>
          <p>{data.client.case_type || 'Expediente legal'}{data.client.case_number ? ` · ${data.client.case_number}` : ''}</p>
          <div className="client-portal-security">Acceso privado para consultar actuaciones y compartir documentos con su equipo legal.</div>
        </header>

        <section className="client-portal-summary" aria-label="Resumen del expediente">
          <div><span>Documentos</span><b>{data.documents.length}</b></div>
          <div><span>Actuaciones recientes</span><b>{data.activities.length}</b></div>
          <div><span>Estado del portal</span><b className="client-portal-status">Protegido</b></div>
        </section>

        <div className="client-portal-layout">
          <section className="client-portal-section client-portal-documents">
            <div className="client-portal-section-heading">
              <div>
                <span className="client-portal-kicker">DOCUMENTOS COMPARTIDOS</span>
                <h2>Documentos del expediente</h2>
                <p>Incluye los archivos compartidos por Abogados Quiroz y los que usted ha enviado.</p>
              </div>
            </div>
            {data.drive_notice && <div className="client-portal-notice">{data.drive_notice}</div>}
            {data.documents.length ? (
              <div className="client-portal-document-list">
                {data.documents.map((row, index) => (
                  <article className="client-portal-document" key={row.file_id || row.id || `${row.name}-${index}`}>
                    <span className={`client-portal-file-icon client-portal-file-icon--${fileIcon(row.mime_type).toLowerCase()}`}>{fileIcon(row.mime_type)}</span>
                    <div className="client-portal-document-main">
                      <b>{row.name}</b>
                      <small>{row.path || 'Expediente'} · {row.type || 'Documento'}{row.date ? ` · ${row.date}` : ''}{fileSize(row.size) ? ` · ${fileSize(row.size)}` : ''}</small>
                      <span className={`client-portal-source client-portal-source--${row.source === 'Cliente' ? 'client' : 'firm'}`}>{row.source === 'Cliente' ? 'Enviado por usted' : 'Compartido por el despacho'}</span>
                    </div>
                    {row.available && row.file_id ? <div className="client-portal-document-actions">
                      <a href={portalFileUrl(token, row.file_id)} target="_blank" rel="noreferrer">Ver</a>
                      <a href={portalFileUrl(token, row.file_id, true)}>Descargar</a>
                    </div> : <span className="client-portal-unavailable">No disponible</span>}
                  </article>
                ))}
              </div>
            ) : <div className="client-portal-empty"><b>Su expediente aún no tiene documentos compartidos.</b><span>Cuando el despacho agregue un archivo, aparecerá aquí.</span></div>}
          </section>

          <aside className="client-portal-side">
            <section className="client-portal-section client-portal-activity">
              <span className="client-portal-kicker">SEGUIMIENTO</span>
              <h2>Actuaciones recientes</h2>
              {data.activities.length ? <ol>
                {data.activities.slice(0, 8).map((row) => <li key={row.id}><b>{row.text}</b><small>{row.date} · {row.user || 'Abogados Quiroz'}</small></li>)}
              </ol> : <p className="client-portal-muted">No hay actuaciones publicadas todavía.</p>}
            </section>

            {data.uploads_enabled && <form className="client-portal-upload" onSubmit={upload}>
              <span className="client-portal-kicker">ENVIAR DOCUMENTO</span>
              <h2>Comparta un archivo con su equipo legal</h2>
              <p>Puede enviar PDF, imágenes, documentos Word y otros archivos de hasta 15 MB.</p>
              <label className="client-portal-file-picker">
                <input type="file" onChange={(event) => setFile(event.target.files?.[0] || null)} />
                <span>{file ? file.name : 'Seleccionar archivo'}</span>
              </label>
              <button disabled={!file || uploading}>{uploading ? 'Subiendo…' : 'Subir al expediente'}</button>
            </form>}
          </aside>
        </div>
        {message && <p className="client-portal-message" role="status">{message}</p>}
      </section>
    </main>
  );
}
